// PRD 07 §2.2 #18 / §3.2 — Patient Screen Preview. Live HTML render of the
// authored content, sanitized to keep arbitrary user input from injecting
// script/handlers when we use `dangerouslySetInnerHTML`. Matches the
// prototype `updatePreview` rendering exactly.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { VideoBlockPreview } from '@/components/data/VideoBlockPreview';
import { sanitizeHtml } from '@/shared/utils/sanitize-html';

import type { ContentBlock } from '../types/management-guidelines.types';

interface MobilePreviewProps {
  // Title falls back to a localized "No title" string when empty so the
  // preview always shows something users can scan visually.
  name: string;
  blocks: ContentBlock[];
}

// Today in YYYY.MM.DD (KST) per PRD §4.4 — matches the prototype
// `new Date().toLocaleDateString('ko-KR')` once you strip the trailing dot.
function todayKst(): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date()).replace(/-/g, '.');
}

function hasAnyContent(blocks: ContentBlock[]): boolean {
  return blocks.some((b) => {
    if (b.title.trim()) return true;
    if (b.text.trim()) return true;
    if (b.type === 'video' && (b.url ?? '').trim()) return true;
    if (b.type === 'image' && b.dataUrl) return true;
    return false;
  });
}

export function MobilePreview({ name, blocks }: MobilePreviewProps) {
  const { t } = useTranslation('management-guidelines');
  const today = useMemo(todayKst, []);
  const showContent = hasAnyContent(blocks);

  return (
    <div className="mobile-preview">
      <div className="mobile-preview__header">
        {t('brand.serviceName', { ns: 'labels' })}
      </div>
      <div className="mobile-preview__content">
        {!showContent ? (
          <div className="mobile-preview__empty">
            {t('preview.empty')}
          </div>
        ) : (
          <>
            <div className="mobile-preview__title">
              {name.trim() || t('preview.untitled')}
            </div>
            <div className="mobile-preview__date">{today}</div>
            <div className="mobile-preview__body">
              {blocks.map((block) => (
                <PreviewBlock key={block.id} block={block} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PreviewBlock({ block }: { block: ContentBlock }) {
  const { t } = useTranslation('management-guidelines');

  if (block.type === 'text') {
    // PRD §3.2 — show only what the admin actually typed. An empty block title
    // must render nothing (no block-type label fallback like "Text").
    const title = block.title.trim();
    const body = block.text.trim();
    const html = body ? sanitizeHtml(body) : '';
    return (
      <div style={{ marginBottom: 8 }}>
        {title ? (
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
        ) : null}
        {html ? (
          // Sanitized via sanitizeHtml above — safe to render as innerHTML.
          <div className="mg-rte-render" dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <span style={{ color: 'var(--muted-foreground)' }}>
            {t('preview.noBody')}
          </span>
        )}
      </div>
    );
  }

  if (block.type === 'image') {
    const title = block.title.trim();
    const desc = block.showText ? block.text.trim() : '';
    const descNode = block.showText ? (
      <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>
        {desc || (
          <span style={{ color: 'var(--muted-foreground)' }}>
            {t('preview.noImageDesc')}
          </span>
        )}
      </div>
    ) : null;

    if (block.dataUrl) {
      return (
        <div style={{ marginBottom: 8 }}>
          {title ? (
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
          ) : null}
          <img
            src={block.dataUrl}
            alt=""
            style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }}
          />
          {descNode}
        </div>
      );
    }
    return (
      <div
        style={{
          marginBottom: 8,
          padding: 8,
          border: '1px dashed var(--border)',
          borderRadius: 8,
        }}
      >
        🖼{title ? ` ${title}` : ''}
        {descNode}
      </div>
    );
  }

  // Video
  const title = block.title.trim();
  const url = (block.url ?? '').trim();
  const desc = block.showText ? block.text.trim() : '';
  const descNode = block.showText ? (
    <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>
      {desc || (
        <span style={{ color: 'var(--muted-foreground)' }}>
          {t('preview.noVideoDesc')}
        </span>
      )}
    </div>
  ) : null;

  return (
    <div
      style={{
        marginBottom: 8,
        padding: 8,
        background: '#eef2ff',
        borderRadius: 8,
      }}
    >
      🎬{title ? ` ${title}` : ''}
      {/* SHCT-648 — a guideline video block carries a URL only, no dataUrl (ADM.018 / ADM.026,
          PRD 07 §4.4). SHCT-495 — embed YouTube/Vimeo; SHCT-370 — an unembeddable URL falls back
          to the "영상 보기" card ADM.044 names, not the raw address. Still no local <video>.
          PRD 10 v2.15 keeps the placeholder for a block whose URL was never entered. */}
      <div style={{ marginTop: 4 }}>
        {url ? (
          <VideoBlockPreview url={url} title={title || 'video'} watchLabel={t('preview.watchVideo')} />
        ) : (
          <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
            {t('preview.noVideoUrl')}
          </div>
        )}
      </div>
      {descNode}
    </div>
  );
}
